import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Auto scroll to an anchor from URL/state (supports dynamic content).
 * @param options - configuration
 * @param options.anchor - explicit anchor id (higher priority than URL hash)
 * @param options.block - scroll position ('start'|'center'|'end')
 * @param options.behavior - scroll behavior ('auto' | 'smooth')
 * @param options.deps - dependencies that should retrigger scrolling
 */
export function useScrollToAnchor(options?: {
    anchor?: string;
    block?: 'start' | 'center' | 'end';
    behavior?: ScrollBehavior;
    deps?: any[];
    /** Extra scroll offset (e.g., compensate for sticky header). Can be negative. */
    offset?: number;
}) {
    const location = useLocation();
    const anchorRef = useRef<HTMLElement>(null);

    useEffect(() => {
        // 1) Resolve target anchor (priority: options > hash > location.state > URL query ?scrollTo=)
        const targetAnchor =
            options?.anchor ||
            window.location.hash.slice(1) ||
            (location.state as any)?.scrollTo ||
            new URLSearchParams(location.search).get('scrollTo') ||
            '';
        if (!targetAnchor) return;

        // 2) Find element by ref first, then by id
        const element = anchorRef.current || document.getElementById(targetAnchor);
        if (!element) return;

        setTimeout(() => {
            element?.scrollIntoView({
                behavior: options?.behavior || 'smooth',
                block: options?.block || 'start',
            });
            if (typeof options?.offset === 'number' && options.offset !== 0) {
                setTimeout(() => {
                    window.scrollBy({ top: options.offset, behavior: options?.behavior || 'smooth' });
                }, 300);
            }
        }, 0);
    }, [location, options?.anchor, options?.block, options?.behavior, options?.offset, ...(options?.deps || [])]);

    // Return ref that can be attached to dynamically rendered element
    return anchorRef;
}
